import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { 
  User, BadgeCheck, MapPin, Calendar, Car, Star, X, MessageCircle 
} from 'lucide-react';
import { getProfilePhotoUrl } from '../../lib/imageUtils';
import { format, parseISO } from 'date-fns';

const HostModal = ({ host, isOpen, onClose, onMessage, api }) => {
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ total_count: 0, average_rating: null });
  const [loadingReviews, setLoadingReviews] = useState(false);

  useEffect(() => {
    if (isOpen && host?.user_id && api) {
      fetchReviews();
    }
  }, [isOpen, host?.user_id]);

  const fetchReviews = async () => {
    if (!host?.user_id || !api) return;
    setLoadingReviews(true);
    try {
      // Use the existing endpoint (backend still uses 'owner' terminology internally)
      const response = await api.get(`/reviews/owner/${host.user_id}?limit=5`);
      setReviews(response.data.reviews || []);
      setReviewStats({
        total_count: response.data.total_count || 0,
        average_rating: response.data.average_rating
      });
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    } finally {
      setLoadingReviews(false);
    }
  };

  if (!host) return null;

  const photoUrl = getProfilePhotoUrl(host.profile_photo);
  const isVerified = host.verification_status === 'verified';
  
  // Parse join date
  let joinDate = null;
  try {
    if (host.created_at) {
      joinDate = parseISO(host.created_at);
    }
  } catch (e) {
    console.error('Error parsing date:', e);
  }

  // Extract suburb/state from location (e.g., "123 Main St, Sydney, NSW" -> "Sydney, NSW")
  const getDisplayLocation = (location) => {
    if (!location) return null;
    const parts = location.split(',').map(p => p.trim());
    if (parts.length >= 2) {
      // Return last 2 parts (suburb, state)
      return parts.slice(-2).join(', ');
    }
    return location;
  };

  const displayLocation = getDisplayLocation(host.location);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto mx-4 max-w-[calc(100vw-2rem)]">
        <DialogHeader className="pb-0">
          <DialogTitle className="font-heading text-lg sr-only">Host Profile</DialogTitle>
          <Button 
            variant="ghost" 
            size="icon" 
            className="absolute right-4 top-4 h-8 w-8"
            onClick={onClose}
            data-testid="close-host-modal"
          >
            <X className="w-4 h-4" />
          </Button>
        </DialogHeader>
        
        <div className="flex flex-col items-center pt-2 pb-4" data-testid="host-modal">
          {/* Large Profile Photo */}
          <div className="relative mb-4">
            {photoUrl ? (
              <img 
                src={photoUrl}
                alt={host.full_name || 'Host'}
                className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover border-4 border-white shadow-lg"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling?.classList?.remove('hidden');
                }}
                data-testid="host-modal-photo"
              />
            ) : null}
            <div className={`w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-[#004B8D] flex items-center justify-center text-white text-3xl font-semibold shadow-lg ${photoUrl ? 'hidden' : ''}`}>
              {host.full_name?.charAt(0)?.toUpperCase() || <User className="w-10 h-10" />}
            </div>
            {isVerified && (
              <div className="absolute -bottom-1 -right-1 bg-green-500 text-white rounded-full p-1.5 shadow-sm">
                <BadgeCheck className="w-4 h-4" />
              </div>
            )}
          </div>

          {/* Name and Verification */}
          <h2 className="font-heading font-bold text-xl text-slate-900 text-center" data-testid="host-modal-name">
            {host.full_name || 'Vehicle Host'}
          </h2>
          {isVerified && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge 
                    variant="secondary" 
                    className="bg-green-100 text-green-700 border-green-200 mt-2 cursor-help"
                    data-testid="host-modal-verified"
                  >
                    <BadgeCheck className="w-3.5 h-3.5 mr-1" />
                    Verified Host
                  </Badge>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs max-w-[180px] text-center">
                  <p>This host has completed identity verification.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {/* Stats Row */}
          <div className="flex items-center justify-center gap-6 mt-4 text-sm text-slate-600">
            {host.vehicle_count !== undefined && (
              <div className="flex items-center gap-1.5" data-testid="host-vehicle-count">
                <Car className="w-4 h-4 text-[#004B8D]" />
                <span>{host.vehicle_count} vehicle{host.vehicle_count !== 1 ? 's' : ''}</span>
              </div>
            )}
            {host.average_rating && (
              <div className="flex items-center gap-1.5" data-testid="host-rating">
                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                <span>{host.average_rating.toFixed(1)}</span>
              </div>
            )}
            {joinDate && (
              <div className="flex items-center gap-1.5" data-testid="host-join-date">
                <Calendar className="w-4 h-4 text-[#00A3BF]" />
                <span>Joined {format(joinDate, 'MMM yyyy')}</span>
              </div>
            )}
          </div>

          {/* Location */}
          {displayLocation && (
            <div className="flex items-center gap-1.5 mt-3 text-slate-600 text-sm" data-testid="host-location">
              <MapPin className="w-4 h-4 text-[#00A3BF]" />
              <span>{displayLocation}</span>
            </div>
          )}

          {/* Full Bio */}
          <div className="w-full mt-6 px-2">
            <h3 className="font-medium text-slate-900 text-sm mb-2">About</h3>
            <p className="text-slate-600 text-sm leading-relaxed" data-testid="host-modal-bio">
              {host.bio || 'This host hasn\'t added a bio yet.'}
            </p>
          </div>

          {/* Host Reviews Section */}
          <div className="w-full mt-6 px-2" data-testid="host-reviews-section">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-slate-900 text-sm">Host Reviews</h3>
              {reviewStats.average_rating && (
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  <span className="font-medium text-sm">{reviewStats.average_rating}</span>
                  <span className="text-slate-500 text-xs">({reviewStats.total_count} review{reviewStats.total_count !== 1 ? 's' : ''})</span>
                </div>
              )}
            </div>
            
            {loadingReviews ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#004B8D]"></div>
              </div>
            ) : reviews.length > 0 ? (
              <div className="space-y-3 max-h-48 overflow-y-auto">
                {reviews.map((review) => (
                  <div key={review.review_id} className="bg-slate-50 rounded-lg p-3" data-testid={`review-${review.review_id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-slate-700">{review.renter?.full_name || 'Anonymous'}</span>
                      <div className="flex items-center gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <Star 
                            key={i} 
                            className={`w-3 h-3 ${i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'}`} 
                          />
                        ))}
                      </div>
                    </div>
                    {review.comment && (
                      <p className="text-xs text-slate-600 line-clamp-2">{review.comment}</p>
                    )}
                    <p className="text-[10px] text-slate-400 mt-1">
                      {format(parseISO(review.created_at), 'd MMM yyyy')}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-4">No reviews yet</p>
            )}
          </div>

          {/* Message Button */}
          <Button 
            onClick={onMessage}
            className="w-full mt-6 bg-[#004B8D] hover:bg-[#003865] h-11"
            data-testid="host-modal-message-btn"
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            Message Host
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HostModal;
